import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface PhotoViewerDialogProps {
    open: boolean;
    onClose: () => void;
    photoUrl: string | null;
    altText?: string;
}

/**
 * PhotoViewerDialog
 * =================
 *
 * Simple dialog component to display a photo in a larger view.
 * Used for viewing user avatars/photos from the Mships table.
 *
 * Props:
 * - open: boolean - Controls dialog visibility
 * - onClose: function - Callback when dialog is closed
 * - photoUrl: string | null - URL of the photo to display
 * - altText: string - Optional alt text for the image
 */
export const PhotoViewerDialog: React.FC<PhotoViewerDialogProps> = ({
    open,
    onClose,
    photoUrl,
    altText = '',
}) => {
    if (!photoUrl) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent
                sx={{
                    padding: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    '&:first-of-type': {
                        paddingTop: 0,
                    },
                }}
            >
                <Box
                    component="img"
                    src={photoUrl}
                    alt={altText}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        borderRadius: 1,
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default PhotoViewerDialog;
